import React, {useCallback, useEffect, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import {List, Typography} from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import {ITaskItem} from '../../../../dto/Task.dto';
import {
  getTaskCountApiPath,
  useRqListTasks,
} from '../../../../react_query/tasks/ListTasks';
import {useRqTaskByRoleId} from '../../../../react_query/tasks/TasksByRole';
import SkeletonNotificationList from '../../../../components/skeleton/SkeletonNotificationList';
import TaskListItemCard from '../../../../components/my-task/TaskListItemCard';
import {IStyleProps} from '../../../staff-landing-page/StaffLandingPage';
import {taskFilterSearchProp} from '../../../staff-landing-page/StaffLandingTaskList';
import ManualTaskViewDialog from './ManualTaskViewDialog';
import OnboardingMessageCreate from '../../../admin/CMS_OPS/Creative/OnboardingMessageTask/OnboardingMessageCreate';
import OnboardingStationsRequest from '../../../admin/CMS_OPS/StationMgmt/OnboardingStationsRequest';
import TnnProducerReviewFile from '../../../admin/tnn-control/task/TnnProducerReviewFile';
import {newTabSlugs} from '../../../task/TaskSlugs';

export const mapTaskRoutes = (taskId: number, slug: string) => {
  let path = '/';
  if (newTabSlugs.includes(slug)) {
    path = `/task/${taskId}`;
  } else if (slug === 'create_dj_stinger_message') {
    path = `/task/${taskId}/onboarding-message`; // don't need to convert to new tab  bcz it has popup dialog
  } else if (slug === 'station_creation') {
    path = `/task/${taskId}/station`; // don't need to convert to new tab bcz it has popup dialog
  }
  return path;
};
type Props = {
  tabValue: string;
  style: IStyleProps;
  searchVal: taskFilterSearchProp;
  setSearchVal: React.Dispatch<React.SetStateAction<taskFilterSearchProp>>;
};
export const TASK_COMPLETED_CHANNEL = 'task_completed_channel';

const TaskList = ({tabValue, style, searchVal, setSearchVal}: Props) => {
  const queryClient = useQueryClient();

  const [activeTaskSlug, setActiveTaskSlug] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [refetchKey, setRefetchKey] = useState(0);
  const [content, setContent] = useState<ITaskItem[]>([]);
  const [open, setOpen] = useState(false);
  const [stationTaskId, setStationTaskId] = useState<number>(0);
  const [taskData, setTaskData] = useState<ITaskItem>();
  const [isGT, setIsGT] = useState<boolean>(false);
  const [manualTaskId, setManualTaskId] = useState(0);
  const prevTabValue = React.useRef(tabValue);
  const {data, isFetched, isError} = useRqListTasks(
    searchVal,
    tabValue,
    tabValue === '0' || tabValue === '4',
  );
  const {
    data: tasks,
    isFetched: tasksisFetched,
    isError: tasksIsError,
    error: tasksError,
  } = useRqTaskByRoleId(
    searchVal,
    tabValue,
    tabValue === '1' || tabValue === '2',
  );

  useEffect(() => {
    if (
      (tabValue === '0' || tabValue === '4') &&
      data &&
      !isError &&
      isFetched
    ) {
      setContent(prev => {
        if (prev.length === 0 || data.meta.current_page === 1) {
          if (data.data.length === 7) {
            setHasMore(true);
          }
          return data.data;
        } else {
          return [...prev, ...data.data];
        }
      });
      if (data.data.length < 7) {
        setHasMore(false);
      }
    }
    if (
      (tabValue === '1' || tabValue === '2') &&
      tasks &&
      !tasksIsError &&
      tasksisFetched
    ) {
      setContent(prev => {
        if (
          prev.length === 0 ||
          tasks.meta.current_page === 1 ||
          refetchKey !== 0
        ) {
          if (tasks.data.length === 7) {
            setHasMore(true);
          }
          return tasks.data;
        } else {
          return [...prev, ...tasks.data];
        }
      });
      if (tasks.data.length < 7) {
        setHasMore(false);
      }
    }
  }, [
    data,
    isError,
    isFetched,
    tabValue,
    tasks,
    tasksError,
    tasksIsError,
    tasksisFetched,
    refetchKey,
  ]);

  useEffect(() => {
    setContent([]);
    setRefetchKey(0);
    setSearchVal({
      type: tabValue === '4' ? 'manual' : '',
      per_page: 7,
      page: 1,
    });
    prevTabValue.current = tabValue;
  }, [tabValue, setSearchVal]);

  useEffect(() => {
    let queryKey = ['v1/task/my-task'];

    if (tabValue === '0' || tabValue === '4') {
      queryKey = ['v1/task/role/list'];
    }

    queryClient.removeQueries({queryKey});
  }, [tabValue, queryClient]);

  const navigate = useNavigate();
  const handleNavigate = (item: ITaskItem) => {
    setRefetchKey(0);
    switch (item.task_slug) {
      case 'manual-task':
        setOpen(true);
        setManualTaskId(item.id);
        break;
      case 'create_dj_stinger_message':
        if (item.company?.product?.cmc_product === 'GameTime') {
          setIsGT(true);
        } else {
          setIsGT(false);
        }
        setTaskData(item);
        setActiveTaskSlug('create_dj_stinger_message');
        break;
      case 'verify-partner-provided-campaign-details':
      case 'reupload-sponsor-rejected-campaign-file':
        setTaskData(item);
        setActiveTaskSlug(item.task_slug);
        break;
      case 'station_creation':
        setStationTaskId(item.id);
        setActiveTaskSlug(item.task_slug);
        break;
      default:
        sessionStorage.setItem('task_id', `${item.id}`);
        if (newTabSlugs.includes(item.task_slug)) {
          handleNewTab(item.id);
        } else {
          navigate(mapTaskRoutes(item.id, item.task_slug), {
            state: {
              company_id: item.company?.id.toString(),
            },
          });
        }
        break;
    }
  };

  const handleNewTab = (id: number) => {
    const url = `task/${id}`;
    window.open(url, '_blank');
  };

  const handleChangePage = useCallback(() => {
    if (tasks?.meta.current_page || data?.meta.current_page) {
      const currentPage = data?.meta.current_page ?? tasks?.meta.current_page;
      setSearchVal(prev => {
        return {...prev, page: Number(currentPage) + 1};
      });
    }
  }, [data?.meta.current_page, tasks?.meta.current_page, setSearchVal]);

  const handleOnboardCancel = () => {
    setTaskData(undefined);
  };

  useEffect(() => {
    const channel = new BroadcastChannel(TASK_COMPLETED_CHANNEL);
    channel.onmessage = event => {
      if (event.data === 'taskCompleted') {
        setContent([]);
        setSearchVal(prev => {
          const newSearch = {...prev, page: 1};
          // Remove any cached role list queries (including ones with higher page numbers)
          // so we don't accidentally reuse paginated cache that goes up to page N.
          queryClient.removeQueries({queryKey: ['v1/task/role/list']});
          // Also remove cached "my task" queries so the my-task list will start from page 1
          queryClient.removeQueries({queryKey: ['v1/task/my-task']});
          return newSearch;
        });

        // Invalidate task count
        queryClient.invalidateQueries({
          queryKey: [getTaskCountApiPath()],
        });
      }
    };

    return () => channel.close();
  }, [queryClient, tabValue, setSearchVal]);
  return (
    <>
      <InfiniteScroll
        className='mt-4'
        dataLength={content.length ?? 0}
        next={handleChangePage}
        scrollableTarget='taskListDiv'
        hasMore={hasMore}
        loader={
          <>
            <SkeletonNotificationList />
            <SkeletonNotificationList />
          </>
        }
        endMessage={
          <Typography className='text-center p-3'>
            You have viewed all tasks.
          </Typography>
        }>
        <List disablePadding id={'taskListDiv'}>
          {content
            ? content?.map(item => (
                <TaskListItemCard
                  key={item.id}
                  task={item}
                  handleNavigate={handleNavigate}
                  tabValue={tabValue}
                  style={style}
                />
              ))
            : null}
        </List>
      </InfiniteScroll>
      <ManualTaskViewDialog open={open} setOpen={setOpen} id={manualTaskId} />
      {taskData && activeTaskSlug === 'create_dj_stinger_message' && (
        <OnboardingMessageCreate
          handleCancel={handleOnboardCancel}
          taskData={taskData}
          setIsGT={setIsGT}
          isGT={isGT}
        />
      )}
      {stationTaskId > 0 && (
        <OnboardingStationsRequest
          taskId={stationTaskId}
          handleCancel={() => setStationTaskId(0)}
        />
      )}
      {taskData &&
        (activeTaskSlug === 'verify-partner-provided-campaign-details' ||
          activeTaskSlug === 'reupload-sponsor-rejected-campaign-file') && (
          <TnnProducerReviewFile
            open={
              activeTaskSlug === 'verify-partner-provided-campaign-details' ||
              activeTaskSlug === 'reupload-sponsor-rejected-campaign-file'
            }
            taskData={taskData}
            setActiveTaskSlug={setActiveTaskSlug}
          />
        )}
    </>
  );
};

export default React.memo(TaskList);
